import Decimal from 'decimal.js';

const PreciseDecimal = Decimal.clone({ precision: 48 });

/** Only sum complete columns with supported units; never present a partial total as a full total. */
export function sumPackingColumn(values: readonly (string | null | undefined)[], kind: 'packages' | 'weight' | 'volume'): string | null {
  if (!values.length) return null;
  let sum = new PreciseDecimal(0);
  let packageUnit: string | null = null;
  for (const value of values) {
    const parsed = parsePackingValue(value, kind);
    if (!parsed) return null;
    if (kind === 'packages') {
      if (packageUnit !== null && parsed.unit !== packageUnit) return null;
      packageUnit = parsed.unit;
    }
    sum = sum.plus(parsed.amount);
  }
  return `${sum.toFixed()}${kind === 'packages' && packageUnit ? ` ${packageUnit}` : ''}`;
}

export function parsePackingValue(value: string | null | undefined, kind: 'packages' | 'weight' | 'volume'): { amount: Decimal; unit: string } | null {
  if (!value) return null;
  const match = /^(\d+(?:\.\d{1,6})?)\s*([a-z³\d\u4e00-\u9fff]*)$/iu.exec(value.trim());
  if (!match) return null;
  const amount = new PreciseDecimal(match[1]);
  const rawUnit = match[2].toUpperCase();
  const units = kind === 'weight' ? ['', 'KG', 'KGS', 'KILOGRAM', 'KILOGRAMS', '千克', '公斤']
    : kind === 'volume' ? ['', 'CBM', 'M3', 'M³', '立方米']
      : ['', 'CTN', 'CTNS', 'CARTON', 'CARTONS', '箱', 'PKG', 'PKGS', 'PACKAGES', '件', 'PALLET', 'PALLETS', '托盘'];
  if (!units.includes(rawUnit) || (kind === 'packages' && !amount.isInteger())) return null;
  const unit = /^(?:CTNS?|CARTONS?|箱)$/u.test(rawUnit) ? 'CTNS' : /^(?:PALLETS?|托盘)$/u.test(rawUnit) ? 'PALLETS' : rawUnit ? 'PKGS' : '';
  return { amount, unit };
}

/** Item prices follow the declared Incoterm; CIF/CFR charges must not be added twice. */
export function calculateCustomsValues(input: { subtotal: string | null; incoterm?: string | null; freight?: string | null; insurance?: string | null }): { fob: string | null; cif: string | null } {
  if (input.subtotal == null || input.freight == null || input.insurance == null) return { fob: null, cif: null };
  const term = input.incoterm?.trim().toUpperCase();
  if (!['FOB', 'CFR', 'CIF'].includes(term ?? '')) return { fob: null, cif: null };
  const subtotal = new PreciseDecimal(input.subtotal);
  const freight = new PreciseDecimal(input.freight);
  const insurance = new PreciseDecimal(input.insurance);
  const fob = term === 'FOB' ? subtotal : term === 'CFR' ? subtotal.minus(freight) : subtotal.minus(freight).minus(insurance);
  if (fob.isNegative()) return { fob: null, cif: null };
  return { fob: fob.toFixed(2), cif: fob.plus(freight).plus(insurance).toFixed(2) };
}
